import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import head from 'lodash/head';
import { Menu, MenuMetadataForChannel, MenuItemGroup, MenuItemGroupType, OrderMeal, PreConfiguredMeal, MealType } from '@/models';
import { slugify } from '@/helpers/formatting';
import { HeaderFlags, FooterMode } from '@/store/ui';
import { isGroupOrderRoute, Routes, doesRouteRequireGroupOrderRestaurant } from '@/router';
import { ProductViewEvent } from '@/services/analytics/events';

@Component({
  name: 'CmgMealBuilderPage',
  beforeRouteLeave(to, from, next) {
    this.$services.ui.popHeaderState();
    this.$services.ui.popFooterState();

    if (isGroupOrderRoute(this.$route)) {
      this.$services.order.setIsGroupOrderParticipant(false);
      if (!doesRouteRequireGroupOrderRestaurant(to)) {
        this.$services.restaurant.selectRestaurant(this.$appStore.state.restaurant.preGroupOrderFlowRestaurant, this.$services.order.isPickingUp());
      }
    }

    next();
  },
  beforeRouteUpdate(to, from, next) {
    this.$services.ui.popHeaderState();
    this.$services.ui.popFooterState();
    next();
  },
})
export default class CmgMealBuilderPage extends Vue {
  get menu(): Menu | null {
    return this.$appStore.state.menu.activeMenu;
  }

  get metadata(): MenuMetadataForChannel | null {
    return this.$appStore.state.menu.metadata;
  }

  get isEditMode(): boolean {
    return !!this.mealId || !!this.pcmId;
  }
  get isPreConfiguredGroup(): boolean {
    return !!this.group && this.group.type == MenuItemGroupType.PreConfigured;
  }

  get preConfiguredMeals(): PreConfiguredMeal[] {
    return this.$appStore.state.menu.preConfiguredMeals.filter(meal => meal.mealType == MealType.Lifestyle);
  }

  get group(): MenuItemGroup | null {
    if (!this.metadata || !this.menu) {
      return null;
    }

    let group: MenuItemGroup | undefined;
    const groups = this.$services.metadata.getValidMenuGroupsForMenu(this.menu, this.metadata, false);

    if (this.isEditMode) {
      let meal;
      if (this.pcmId) {
        meal = head(this.$appStore.state.menu.preConfiguredMeals.filter(m => m.mealId == this.pcmId));
      } else {
        const order = this.$services.order.getCurrentOrder();
        if (!order || !order.order.meals) {
          // cannot edit a meal if it doesn't actually exist on order
          return null;
        }

        meal = order.order.meals.find(m => m.mealId == this.mealId);
        if (!meal) {
          // cannot edit a meal if it doesn't actually exist on order
          return null;
        }
      }

      const menuItemId = this.mealId
        ? this.$services.menu.getFirstTopLevelMenuItemId(meal as OrderMeal)
        : this.$services.menu.getFirstTopLevelMenuItemIdForPcm(meal as PreConfiguredMeal);
      group = this.$services.metadata.getFirstGroupContainingMenuItem(menuItemId) || undefined;
    } else {
      const groupName = this.$route.params['groupName'];
      group = groups.find(g => slugify(g.displayName) == groupName);
    }

    // Find the group matching the group name
    if (!group) {
      // if the group name is not properly defined in the menu meta-data the UI will be messed up
      return null;
    }

    this.$services.googleAnalytics.sendProductDetailViewEvent(group.displayName);
    return group;
  }

  get rootGroup(): MenuItemGroup | null {
    const group = this.group;
    if (!group || !this.menu || !this.metadata) {
      return null;
    }

    const groups = this.$services.metadata.getValidMenuGroupsForMenu(this.menu, this.metadata, false);

    // See if this group is actually part of a quantity group and select the quantity group instead
    const quantityGroup = groups.find(g => g.type == MenuItemGroupType.Quantity && !!g.groups.find(qg => qg.menuItemGroupId == group.id)) || null;
    return quantityGroup || group;
  }

  get selectedQuantityGroup(): MenuItemGroup | null {
    // If the group is the same as the root group, no trickery going on
    if (this.group == this.rootGroup) {
      return null;
    }

    // Otherwise, if the root group is of type quantity, then the group must be a selected quantity
    if (!!this.rootGroup && this.rootGroup.type == MenuItemGroupType.Quantity) {
      return this.group;
    }

    return null;
  }

  get mealId(): string {
    return this.$route.params.mealId;
  }

  get pcmId(): string {
    return this.$route.params.pcmId;
  }

  mounted() {

    this.$services.ui.pushFooterState({ mode: FooterMode.None });

    if (isGroupOrderRoute(this.$route)) {
      if (!this.$appStore.state.order.isGroupOrderHost) {
        this.$services.order.setIsGroupOrderParticipant(true);
      }

      const groupRoute = this.$route.params.returnRoute || '/order/group/menu';
      this.$services.ui.pushHeaderState({ flags: HeaderFlags.LogoVisibleXS | HeaderFlags.Bag | HeaderFlags.ReturnToRoute, route: groupRoute, routeText: 'Return to Menu' });
      this.$services.branch.viewMenuItemEvent();
      return;
    }

    this.$services.branch.viewMenuItemEvent();

    const flags = HeaderFlags.All & ~HeaderFlags.LogoVisibleXS & ~HeaderFlags.MenuNav & ~HeaderFlags.FindChipotleLeft;
    const route = this.$route.params.returnRoute || '/#menu';
    this.$services.ui.pushHeaderState({ flags, route, routeText: 'Return to Menu' });
    
    this.$services.analytics.send(new ProductViewEvent(this.$router.currentRoute.params.groupName ?? ''));
  }

  onMealBuilderModeChanged(): void {
    this.$services.adobeTarget.sendAdobeTargetExperienceRequest(this.$route.name);
  }
}
